import type { MajorStatus, MinorStatus } from "@citrine/types/status";

import type { EntityMetadata } from "../common";
import type { StatusDetail } from "../common/status";
import { isOfType } from "../utils";

export const EVALUATOR_METRIC_TYPES = [
  "CoverageProbability",
  "F1",
  "AreaUnderROC",
  "PVA",
  "RSquared",
  "RMSE",
  "StandardRMSE",
  "NDME",
] as const;

export type EvaluatorMetricType = (typeof EVALUATOR_METRIC_TYPES)[number];

interface BaseEvaluatorMetric {
  type: EvaluatorMetricType;
}

interface BasicEvalutorMetric extends BaseEvaluatorMetric {
  type: Exclude<EvaluatorMetricType, "CoverageProbability">;
}

export interface CoverageProbabilityMetric extends BaseEvaluatorMetric {
  type: "CoverageProbability";
  coverage_level?: string | number;
}
export type EvaluatorMetric = BasicEvalutorMetric | CoverageProbabilityMetric;
export const isCoverageProbabilityMetric = isOfType<
  CoverageProbabilityMetric,
  EvaluatorMetric
>("CoverageProbability");

export const DEFAULT_COVERAGE_LEVEL = 0.683;
export const DEFAULT_N_FOLDS = 5;
export const DEFAULT_N_TRIALS = 3;

export interface CrossValidationEvaluator {
  type: "CrossValidationEvaluator";
  name: string;
  description: string;
  responses: string[];
  n_folds: number;
  n_trials: number;
  metrics: EvaluatorMetric[];
  ignore_when_grouping: string[];
}

// we currently only have 1 evaluator type, but that will change
export type PredictorEvaluator = CrossValidationEvaluator;

export const isCrossValidationEvaluator = (
  pe: PredictorEvaluator,
): pe is CrossValidationEvaluator => pe.type === "CrossValidationEvaluator";

export interface PredictorEvaluationData {
  evaluators: PredictorEvaluator[];
}

export interface Status {
  major: MajorStatus;
  minor: MinorStatus;
  detail: StatusDetail[];
}

export interface PredictorEvaluationMetadata extends Omit<
  EntityMetadata,
  "status"
> {
  status: Status;
  predictor_id: string;
  predictor_version: number;
}

export interface PredictorEvaluation {
  id: string;
  data: PredictorEvaluationData;
  metadata: PredictorEvaluationMetadata;
  results?: PredictorEvaluationResults;
}

export interface RealMetricValue {
  type: "RealMetricValue";
  mean: number;
  standard_error?: number;
}

interface PredictedVsActualValueBase {
  uuid: string;
  identifiers: string[];
  trial: number;
  fold: number;
}

// Mimic behavior of getMaterialNameFromIdentifiers
export function getMaterialNameFromPredictedVsActualValue(
  value: PredictedVsActualValueBase,
) {
  return value.identifiers.length ? value.identifiers.join(" ") : value.uuid;
}

export interface CategoricalPredictedVsActualValue<
  T extends string = string,
> extends PredictedVsActualValueBase {
  predicted: Partial<Record<T, number>>;
  actual: Partial<Record<T, number>>;
}

export interface CategoricalPredictedVsActual<T extends string = string> {
  type: "CategoricalPredictedVsActual";
  value: CategoricalPredictedVsActualValue<T>[];
}

export interface RealPredictedVsActualValue extends PredictedVsActualValueBase {
  predicted: RealMetricValue;
  actual: RealMetricValue;
}

export interface RealPredictedVsActual {
  type: "RealPredictedVsActual";
  value: RealPredictedVsActualValue[];
}

export type PredictedVsActualValue<T extends string = string> =
  CategoricalPredictedVsActualValue<T> | RealPredictedVsActual;

export type PredictedVsActual<T extends string = string> =
  CategoricalPredictedVsActual<T> | RealPredictedVsActual;

export const isCategoricalPredictedVsActual = (
  pva?: PredictedVsActual,
): pva is CategoricalPredictedVsActual =>
  pva?.type === "CategoricalPredictedVsActual";

export const isRealPredictedVsActual = (
  pva?: PredictedVsActual,
): pva is RealPredictedVsActual => pva?.type === "RealPredictedVsActual";

export interface Metrics {
  area_under_roc?: RealMetricValue;
  f1?: RealMetricValue;
  ndme?: RealMetricValue;
  predicted_vs_actual?: PredictedVsActual;
  rmse?: RealMetricValue;
  "R^2"?: RealMetricValue;
  standardized_rmse?: RealMetricValue;
}

export interface DownsamplingConfig {
  was_downsampled: boolean;
  downsampling_max?: number;
  original_pva_count: number;
  downsampled_pva_count?: number;
}

export interface CrossValidationResult {
  type: "CrossValidationResult";
  evaluator: CrossValidationEvaluator;
  response_results: Partial<Record<string, { metrics: Metrics }>>;
  downsampling_config?: DownsamplingConfig;
}

export type PredictorEvaluationResults = CrossValidationResult;

export const isPredictorEvaluationSettled = (
  predictorEvaluation?: PredictorEvaluation | null,
) => {
  return predictorEvaluation?.metadata.status.major !== "INPROGRESS";
};

export const isPredictorEvaluationFailed = (
  predictorEvaluation?: PredictorEvaluation | null,
) => {
  return predictorEvaluation?.metadata.status.major === "FAILED";
};
export const isPredictorEvaluationReady = (
  predictorEvaluation?: PredictorEvaluation | null,
) => {
  return predictorEvaluation?.metadata.status.major === "SUCCEEDED";
};
